import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, ChangeDetectionStrategy, Inject, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnShare } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SN_DRAWER_DATA, SnDrawerRef } from '@lib/ui/drawer';

import { ISnCanShare } from '../../interfaces/can-share';
import { SnShareFieldsGeneratorService } from '../../services/share-fields-generator.service';

export interface IShareConfigDrawerData {
  share: SnShare;
  updateHandler?: ISnCanShare['updateShare'];
}

@Component({
  selector: 'sn-edit-share-drawer',
  templateUrl: './share-config-drawer.container.html',
  styleUrls: ['./share-config-drawer.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareConfigDrawerContainer implements OnDestroy {
  public readonly form = new UntypedFormGroup({});
  public readonly formFields: FormlyFieldConfig[];
  public formModel: any = {};

  public readonly share: SnShare;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _drawerRef: SnDrawerRef,
    @Inject(SN_DRAWER_DATA) private readonly _data: IShareConfigDrawerData,
    private readonly _shareFieldsGenerator: SnShareFieldsGeneratorService,
  ) {
    this.share = this._data.share;

    this.formFields = this._getFormFields();
    this.formModel = this._data.share.toJSON({ groups: ['form'] });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this._data
      .updateHandler(this.share.object.id, this.share.id, this.formModel)
      .pipe(takeUntil(this._destroy$))
      .subscribe((share: SnShare) => {
        this._drawerRef.close(share);
      });
  }

  private _getFormFields(): FormlyFieldConfig[] {
    const additionalFields = this._shareFieldsGenerator.getAdditionalFields(
      this._data.share.object.contentType,
    );

    return [
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.dateLimits'),
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'date-row',
            fieldGroup: [
              this._shareFieldsGenerator.getStartDateField({
                className: 'start-date-col',
                templateOptions: {
                  maxDate: () => this.formModel.endDate,
                },
              }),
              this._shareFieldsGenerator.getEndDateField({
                className: 'end-date-col',
                templateOptions: {
                  minDate: () => this.formModel.startDate,
                },
              }),
            ],
          },
        ],
      },
      {
        wrappers: ['form-field-group'],
        templateOptions: {
          label: t('shares.additionalOptions'),
        },
        hideExpression: !additionalFields.length,
        fieldGroup: additionalFields,
      },
    ];
  }
}
